/* ==========================================================================
   Void Carousel - Loid Shopify Theme
   Two carousel types: VoidSnapCarousel (paginated) & VoidInfiniteCarousel (looping)
   Vanilla JS, no dependencies.
   ========================================================================== */

/* --------------------------------------------------------------------------
   VoidSnapCarousel
   - Horizontal scroll-snap container with pagination dots
   - Active dot tracking via IntersectionObserver
   - Click dot to scroll to that slide
   -------------------------------------------------------------------------- */

class VoidSnapCarousel {
  #container;
  #track;
  #slides;
  #dotsContainer;
  /** @type {HTMLElement[]} */
  #dots = [];
  #activeIndex = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {number | null} */
  #autoScrollTimer = null;
  #autoScrollIndex = 0;
  #hasAutoScrolled = false;
  /** @type {IntersectionObserver | null} */
  #viewObserver = null;
  /** @type {(() => void) | null} */
  #stopAutoScrollHandler = null;

  /**
   * @param {HTMLElement} container - Element with .void-snap-carousel
   */
  constructor(container) {
    this.#container = container;
    this.#track = container; // The container itself scrolls
    this.#slides = Array.from(container.children).filter(
      (child) =>
        !child.classList.contains('void-snap-carousel__dots') &&
        !child.hasAttribute('aria-hidden')
    );
    this.#dotsContainer = container.querySelector('.void-snap-carousel__dots');

    if (this.#slides.length < 2) return;

    this.#buildDots();
    this.#observeSlides();
    this.#autoScrollOnView();

    container.setAttribute('data-void-carousel-init', 'true');
  }

  /* -- Pagination dots --------------------------------------------------- */

  #buildDots() {
    if (!this.#dotsContainer) return;

    this.#dotsContainer.replaceChildren();

    this.#slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'void-snap-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('type', 'button');

      if (index === 0) dot.classList.add('is-active');

      dot.addEventListener('click', () => this.#goToSlide(index));
      this.#dotsContainer?.appendChild(dot);
      this.#dots.push(dot);
    });
  }

  /* -- Active slide tracking via IntersectionObserver --------------------- */

  #observeSlides() {
    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = this.#slides.indexOf(entry.target);
            if (index !== -1) this.#setActive(index);
          }
        }
      },
      {
        root: this.#container,
        threshold: 0.5,
      }
    );

    for (const slide of this.#slides) {
      this.#observer.observe(slide);
    }
  }

  /* -- Navigation -------------------------------------------------------- */

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    const slide = this.#slides[index];
    if (!slide) return;

    slide.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  /* -- Auto-scroll when section enters viewport -------------------------- */

  #autoScrollOnView() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.#viewObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#hasAutoScrolled) {
            this.#hasAutoScrolled = true;
            setTimeout(() => this.#startAutoScroll(), 1500);
          }
        }
      },
      { threshold: 0.2 }
    );

    this.#viewObserver.observe(this.#container);
  }

  #startAutoScroll() {
    this.#autoScrollIndex = 0;

    this.#stopAutoScrollHandler = () => {
      if (this.#autoScrollTimer) {
        clearTimeout(this.#autoScrollTimer);
        this.#autoScrollTimer = null;
      }
      if (this.#stopAutoScrollHandler) {
        this.#container.removeEventListener('touchstart', this.#stopAutoScrollHandler);
        this.#container.removeEventListener('mousedown', this.#stopAutoScrollHandler);
      }
    };
    this.#container.addEventListener('touchstart', this.#stopAutoScrollHandler, { once: true });
    this.#container.addEventListener('mousedown', this.#stopAutoScrollHandler, { once: true });

    const advance = () => {
      this.#autoScrollIndex++;
      if (this.#autoScrollIndex >= this.#slides.length) return;
      this.#goToSlide(this.#autoScrollIndex);
      this.#autoScrollTimer = setTimeout(advance, 3000);
    };

    this.#autoScrollTimer = setTimeout(advance, 3000);
  }

  /* -- Cleanup ----------------------------------------------------------- */

  destroy() {
    this.#observer?.disconnect();
    this.#viewObserver?.disconnect();
    if (this.#autoScrollTimer) clearTimeout(this.#autoScrollTimer);
    this.#dotsContainer?.replaceChildren();
    this.#container.removeAttribute('data-void-carousel-init');
  }
}

/* --------------------------------------------------------------------------
   VoidInfiniteCarousel
   - Duplicates children for seamless infinite loop
   - Driven by void-scroll-left keyframe from void-animations.css
   - Pauses on hover
   - Configurable speed via data-void-speed
   -------------------------------------------------------------------------- */

class VoidInfiniteCarousel {
  #container;
  #track;
  #speed;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /**
   * @param {HTMLElement} container - Element with .void-infinite-carousel
   */
  constructor(container) {
    this.#container = container;
    this.#track = /** @type {HTMLElement} */ (container.querySelector('.void-infinite-carousel__track')) || container;
    this.#speed = container.getAttribute('data-void-speed') || '30s';

    const children = Array.from(this.#track.children);
    if (children.length === 0) return;

    this.#duplicate(children);
    this.#applyAnimation();
    this.#bindHover();
    this.#observeResize();

    container.setAttribute('data-void-carousel-init', 'true');
  }

  /* -- Duplicate children for seamless loop ------------------------------ */

  /**
   * @param {Element[]} children
   */
  #duplicate(children) {
    for (const child of children) {
      const clone = child.cloneNode(true);
      /** @type {Element} */ (clone).setAttribute('aria-hidden', 'true');
      this.#track.appendChild(clone);
    }
  }

  /* -- Animation --------------------------------------------------------- */

  #applyAnimation() {
    const children = Array.from(this.#track.children);
    const half = children.length / 2;
    let width = 0;
    for (let i = 0; i < half; i++) {
      const child = children[i];
      if (child) {
        width += child.offsetWidth;
      }
    }
    const gap = parseFloat(getComputedStyle(this.#track).gap) || 0;
    width += half * gap;

    this.#track.style.setProperty('--void-scroll-distance', `-${width}px`);
    this.#track.style.animation = `void-scroll-left ${this.#speed} linear infinite`;
  }

  #bindHover() {
    this.#container.addEventListener('mouseenter', () => {
      this.#track.style.animationPlayState = 'paused';
    });

    this.#container.addEventListener('mouseleave', () => {
      this.#track.style.animationPlayState = 'running';
    });
  }

  /* -- Resize handling --------------------------------------------------- */

  #observeResize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#track.style.animation = 'none';
      // Use rAF to batch reflow instead of forcing synchronous layout
      requestAnimationFrame(() => {
        this.#applyAnimation();
      });
    });

    this.#resizeObserver.observe(this.#container);
  }

  /* -- Cleanup ----------------------------------------------------------- */

  destroy() {
    this.#resizeObserver?.disconnect();
    this.#track.style.animation = '';
    this.#track.style.animationPlayState = '';

    const children = Array.from(this.#track.children);
    const half = Math.ceil(children.length / 2);
    for (const child of children.slice(half)) {
      child.remove();
    }

    this.#container.removeAttribute('data-void-carousel-init');
  }
}

/* --------------------------------------------------------------------------
   Auto-initialization
   -------------------------------------------------------------------------- */

(() => {
  /** @type {VoidSnapCarousel[]} */
  const snapInstances = [];
  /** @type {VoidInfiniteCarousel[]} */
  const infiniteInstances = [];

  /**
   * Initialize all carousels found within a root element.
   * Skips containers already initialized (data-void-carousel-init).
   * @param {HTMLElement | Document} [root]
   */
  const initCarousels = (root) => {
    const scope = root || document;

    scope.querySelectorAll('.void-snap-carousel:not([data-void-carousel-init])').forEach((el) => {
      snapInstances.push(new VoidSnapCarousel(/** @type {HTMLElement} */ (el)));
    });

    scope.querySelectorAll('.void-infinite-carousel:not([data-void-carousel-init])').forEach((el) => {
      infiniteInstances.push(new VoidInfiniteCarousel(/** @type {HTMLElement} */ (el)));
    });
  };

  document.addEventListener('DOMContentLoaded', () => initCarousels());

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) initCarousels(/** @type {HTMLElement} */ (event.target));
  });
})();
